<script lang="ts">
export default {
  name: 'CoverageLinesEditModal',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ParsedField } from '@/types/field';
import type { FieldOverride } from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import AppModal from '@/components/v2/AppModal.vue';
import EditableField from '@/components/document/EditableField.vue';
import { formatCurrency } from '@/utilities/Money';
import { isCurrencyField } from '@/utilities/CurrencyFields';
import { Pencil, X } from '@lucide/vue';

const props = defineProps<{
  show: boolean;
  coverageLinesField: ParsedField | null;
  coverageLineItems: ParsedField[];
  allFields: ParsedField[];
  isReviewMode: boolean;
  getOverride: (path: string) => FieldOverride | undefined;
  isPending: (path: string) => boolean;
  getError: (path: string) => string | undefined;
  hasLocalChange: (path: string) => boolean;
  getLocalChange: (path: string) => string | number | boolean | undefined;
  setLocalOverride: (path: string, value: string | number | boolean) => void;
  revertLocalOverride: (path: string) => void;
  clearError: (path: string) => void;
  // Structural coverage-line controls (owned by the parent panel; all optional so the
  // modal degrades to read-only headers when they are not provided).
  availableCoverageTypes?: CoverageType[];
  pendingTypeChange?: (item: ParsedField) => string | undefined;
  applyRecategorize?: (item: ParsedField, event: Event) => void;
  undoRecategorize?: (item: ParsedField) => void;
  isLineDeleted?: (item: ParsedField) => boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

// Which column's coverage-type select is open, keyed by the line item's path.
const recatOpenFor = ref<string | null>(null);
watch(
  () => props.show,
  () => {
    recatOpenFor.value = null;
  },
);

const lineDeleted = (item: ParsedField): boolean => props.isLineDeleted?.(item) ?? false;

const canRecategorize = (item: ParsedField): boolean =>
  props.isReviewMode &&
  !!props.applyRecategorize &&
  !lineDeleted(item) &&
  (props.availableCoverageTypes?.length ?? 0) > 0;

const toggleRecat = (item: ParsedField): void => {
  if (!item.path) return;
  recatOpenFor.value = recatOpenFor.value === item.path ? null : item.path;
};

const onRecategorize = (item: ParsedField, event: Event): void => {
  props.applyRecategorize?.(item, event);
  recatOpenFor.value = null;
};

// Index leaf fields by path for quick lookup
const fieldsByPath = computed<Map<string, ParsedField>>(() => {
  const map = new Map<string, ParsedField>();
  for (const f of props.allFields) {
    if (f.path) map.set(f.path, f);
  }
  return map;
});

// Direct (depth+1) leaf children of a given coverage line item
const childFieldsOf = (itemPath: string, itemDepth: number): ParsedField[] => {
  const prefix = `${itemPath}.`;
  return props.allFields.filter(
    (f) =>
      f.path &&
      f.path.startsWith(prefix) &&
      f.depth === itemDepth + 1 &&
      // exclude further-nested grandchildren (already covered by depth check, but be safe)
      !f.path.slice(prefix.length).includes('.'),
  );
};

// Column header for a coverage line item: prefer the coverageTypeName from the schema metadata
const columnLabel = (item: ParsedField, index: number): string => {
  if (item.coverageTypeName) return item.coverageTypeName;
  return item.name && item.name !== '_coverageLineItem' ? item.name : `Coverage ${index + 1}`;
};

// Union of leaf field names across all items, preserving insertion order from items
const fieldRowNames = computed<string[]>(() => {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const item of props.coverageLineItems) {
    if (!item.path || item.depth === undefined) continue;
    for (const child of childFieldsOf(item.path, item.depth)) {
      if (!seen.has(child.name)) {
        seen.add(child.name);
        names.push(child.name);
      }
    }
  }
  return names;
});

// For a given (item, fieldName), return the matching leaf ParsedField if present
const cellField = (item: ParsedField, fieldName: string): ParsedField | undefined => {
  if (!item.path) return undefined;
  return fieldsByPath.value.get(`${item.path}.${fieldName}`);
};

// Display value for a cell (considering overrides) when not editable
const cellDisplay = (field: ParsedField | undefined): string => {
  if (!field) return '—';
  if (field.path) {
    const override = props.getOverride(field.path);
    if (override && override.overrideValue !== null && override.overrideValue !== undefined) {
      return formatValue(override.overrideValue, field);
    }
  }
  if (field.value === null || field.value === undefined || field.value === '') return '—';
  return formatValue(field.value, field);
};

const formatValue = (value: unknown, field?: ParsedField): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  if (isCurrencyField(field)) {
    const formatted = formatCurrency(value as number | string, true);
    if (formatted) return formatted;
  }
  return String(value);
};

const onClose = () => emit('close');
</script>

<template>
  <AppModal
    :show="show"
    title="Coverage Details"
    size="xl">
    <template #actions>
      <button
        class="btn btn-sm"
        @click="onClose">
        Close
      </button>
    </template>

    <template #content>
      <div
        v-if="coverageLineItems.length === 0"
        class="py-8 text-center text-sm text-slate-500">
        No coverage lines extracted.
      </div>
      <div
        v-else
        class="always-show-scrollbar overflow-auto">
        <table
          class="table border-collapse table-zebra"
          :class="isReviewMode ? 'text-sm' : 'text-xs'">
          <thead class="bg-slate-50 text-slate-700">
            <tr>
              <th
                class="sticky left-0 z-10 bg-slate-50 text-left font-semibold"
                :class="isReviewMode ? 'px-4 py-2' : 'px-2 py-1'">
                Field
              </th>
              <th
                v-for="(item, index) in coverageLineItems"
                :key="item.path || index"
                class="text-left font-semibold whitespace-nowrap"
                :class="isReviewMode ? 'min-w-[10rem] px-4 py-2' : 'min-w-[6rem] px-2 py-1'">
                <div class="flex items-center gap-1">
                  <span
                    :class="[
                      'rounded-full px-2 py-0.5 text-[10px] font-medium',
                      lineDeleted(item) ? 'bg-red-100 text-red-700 line-through' : 'bg-green-100 text-green-700',
                    ]">
                    {{ columnLabel(item, index) }}
                  </span>
                  <span
                    v-if="pendingTypeChange?.(item)"
                    class="flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                    &rarr; {{ pendingTypeChange!(item) }}
                    <button
                      type="button"
                      class="rounded hover:bg-amber-200"
                      aria-label="Undo coverage type change"
                      title="Undo coverage type change"
                      @click.stop="undoRecategorize?.(item)">
                      <X class="h-3 w-3" />
                    </button>
                  </span>
                  <button
                    v-if="canRecategorize(item)"
                    type="button"
                    class="flex h-5 w-5 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100"
                    aria-label="Change coverage type"
                    title="Change coverage type"
                    @click.stop="toggleRecat(item)">
                    <Pencil class="h-3 w-3" />
                  </button>
                </div>
                <select
                  v-if="recatOpenFor === item.path"
                  class="mt-1 block rounded border border-slate-300 bg-white px-1 py-0.5 text-xs font-normal"
                  aria-label="Change coverage type"
                  @change="onRecategorize(item, $event)">
                  <option
                    value=""
                    disabled
                    selected>
                    Change type…
                  </option>
                  <option
                    v-for="ct in availableCoverageTypes"
                    :key="ct.coverageTypeId"
                    :value="ct.coverageTypeId">
                    {{ ct.coverageType }}
                  </option>
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="fieldName in fieldRowNames"
              :key="fieldName">
              <td
                class="sticky left-0 bg-base-100 font-medium text-slate-900 whitespace-nowrap"
                :class="isReviewMode ? 'px-4 py-3' : 'px-2 py-1'">
                {{ fieldName }}
              </td>
              <td
                v-for="(item, index) in coverageLineItems"
                :key="(item.path || index) + ':' + fieldName"
                class="align-middle"
                :class="isReviewMode ? 'min-w-[10rem] px-4 py-3' : 'min-w-[6rem] px-2 py-1'">
                <template v-if="cellField(item, fieldName)">
                  <EditableField
                    v-if="isReviewMode && cellField(item, fieldName)!.isEditable && cellField(item, fieldName)!.path"
                    :field="cellField(item, fieldName)!"
                    :override="getOverride(cellField(item, fieldName)!.path!) || null"
                    :is-pending="isPending(cellField(item, fieldName)!.path!)"
                    :error="getError(cellField(item, fieldName)!.path!) || null"
                    :has-local-change="hasLocalChange(cellField(item, fieldName)!.path!)"
                    :local-change-value="getLocalChange(cellField(item, fieldName)!.path!)"
                    :disabled="lineDeleted(item)"
                    :currency-format="isCurrencyField(cellField(item, fieldName))"
                    @update:value="(value) => setLocalOverride(cellField(item, fieldName)!.path!, value)"
                    @revert="revertLocalOverride(cellField(item, fieldName)!.path!)"
                    @clear-error="clearError(cellField(item, fieldName)!.path!)"
                    @clear-pending="revertLocalOverride(cellField(item, fieldName)!.path!)" />
                  <span
                    v-else
                    class="block text-slate-700 whitespace-nowrap">
                    {{ cellDisplay(cellField(item, fieldName)) }}
                  </span>
                </template>
                <span
                  v-else
                  class="text-slate-400">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>
/* Force the scrollbar to remain visible whenever the table overflows,
   regardless of OS auto-hide behavior (e.g. macOS) or browser defaults. */
.always-show-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgb(148 163 184) transparent;
}
.always-show-scrollbar::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.always-show-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.always-show-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225); /* slate-300 */
  border-radius: 9999px;
}
.always-show-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184); /* slate-400 */
}
</style>
